import moment from "moment-timezone";
import { useMemo } from "react";
import { Grid, GridProps } from "@mui/material";
import { GetProductResponse, Promotion } from "src/models/product";
import { CurrencyDisplay } from "../CurrencyDisplay";
import Permission from "src/components/Permission/Permission";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { formatDateOffset } from "src/utils/formatUtil";
import { UserProfile } from "src/infrastructure/auth/UserProfile";
import { useCountry } from "src/components/Context/CountryContext";

type PriceProps = GridProps & {
  product: GetProductResponse;
  promos?: Promotion[];
  userProfile: UserProfile;
};

export const Price: React.FC<PriceProps> = ({ product, userProfile, promos, ...rest }: PriceProps) => {
  const { timezone } = useCountry();

  const activePromoEndDate = useMemo(() => {
    if (!promos) return null;
    const now = moment();
    return (
      promos
        .filter((promo) => promo.dateFrom.isBefore(now) && promo.dateTo.isAfter(now))
        .sort((a, b) => a.dateTo.diff(b.dateTo))[0]?.dateTo ?? null
    );
  }, [promos]);

  return (
    <Grid container {...rest}>
      <Grid item xs={12} component="h3" marginBottom={(theme) => `${theme.spacing(1)} !important`}>
        {product.pricing.isDutyFree ? "Price (Ex GST)" : "Price"}
      </Grid>

      <Grid item xs={6} md={8} lg={6}>
        Ticket
      </Grid>
      <Grid item xs={6} md={4} lg={6}>
        <CurrencyDisplay value={product.pricing.ticketPrice} />
      </Grid>

      {product.pricing.promotionPrice !== null && (
        <>
          <Grid item xs={6} md={8} lg={6}>
            Promotion
          </Grid>

          <Grid item xs={6} md={4} lg={6}>
            <CurrencyDisplay as="strong" value={product.pricing.promotionPrice} />
          </Grid>

          {activePromoEndDate && (
            <>
              <Grid item xs={6} md={8} lg={6}>
                Promo End Date
              </Grid>
              <Grid item xs={6} md={4} lg={6}>
                {moment.tz(activePromoEndDate, timezone).format("DD/MM/YYYY")}
              </Grid>
            </>
          )}
        </>
      )}

      {product.pricing.clearancePrice !== null && (
        <>
          <Grid item xs={6} md={8} lg={6}>
            Clearance
          </Grid>

          <Grid item xs={6} md={4} lg={6}>
            <CurrencyDisplay as="strong" value={product.pricing.clearancePrice} />
          </Grid>
        </>
      )}

      {!product.isMarketplace && (
        <>
          <Permission userRoles={userProfile.roles} componentName={UserRoles.ProductStaffPrice}>
            <Grid item xs={6} md={8} lg={6}>
              Staff
            </Grid>
            <Grid item xs={6} md={4} lg={6}>
              <CurrencyDisplay value={product.pricing.staffPrice} />
            </Grid>
          </Permission>

          <Permission userRoles={userProfile.roles} componentName={UserRoles.ProductGoCost}>
            <Grid item xs={6} md={8} lg={6}>
              Go Cost
            </Grid>
            <Grid item xs={6} md={4} lg={6}>
              <CurrencyDisplay value={product.pricing.goCost} />
            </Grid>
          </Permission>

          <Permission userRoles={userProfile.roles} componentName={UserRoles.ProductGoSell}>
            <Grid item xs={6} md={8} lg={6}>
              Go Sell
            </Grid>
            <Grid item xs={6} md={4} lg={6}>
              <CurrencyDisplay value={product.pricing.goSellPrice} />
            </Grid>
          </Permission>

          <Permission userRoles={userProfile.roles} componentName={UserRoles.ProductAverageCostEx}>
            <Grid item xs={6} md={8} lg={6}>
              Avg Cost Ex
            </Grid>
            <Grid item xs={6} md={4} lg={6}>
              <CurrencyDisplay value={product.pricing.averageCostEx} />
            </Grid>
          </Permission>

          <Permission userRoles={userProfile.roles} componentName={UserRoles.ProductUnitCostEx}>
            <Grid item xs={6} md={8} lg={6}>
              Unit Cost Ex
            </Grid>
            <Grid item xs={6} md={4} lg={6}>
              <CurrencyDisplay value={product.pricing.unitCostEx} />
            </Grid>
          </Permission>
        </>
      )}

      <Grid item xs={6} md={8} lg={6}>
        Last Updated
      </Grid>
      <Grid item xs={6} md={4} lg={6}>
        {formatDateOffset(product.pricing.pricingLastUpdated, "DD/MM/YY h:mm A")}
      </Grid>
    </Grid>
  );
};
